import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Chip, Skeleton, Stack, Typography } from '@mui/material'
import { PROFILE_SKILLS, type StudentLanguageProfile } from '@/api/placement'
import { formatDateDMY } from '@/utils/date'
import { PROBE_SKILLS, skillMeasurementLabel } from '@/utils/placementLabels'

interface LanguageGoalSectionProps {
  profile: StudentLanguageProfile | null
  loading: boolean
  failed: boolean
}

/**
 * Цель обучения, желаемое расписание и уровень — в блоке анкеты.
 *
 * Живут они не в каталоге полей анкеты, а в языковом профиле, и это не
 * формальность: анкета привязана к договору, а профиль — к человеку. У ученика
 * двух учителей цель и уровень одни, и каталожное поле развело бы их надвое.
 * Показываются здесь же, чтобы не искать второй экран, а правятся общей
 * кнопкой блока — своей у раздела нет намеренно, две кнопки «Редактировать»
 * подряд заставляли гадать, какая за что отвечает.
 *
 * Уровень — только на чтение и отдельно от остального: это измерение, а не
 * предпочтение. Разреши его править здесь — и профиль перестанет что-либо
 * значить для программы обучения, потому что каждый поставит себе желаемое.
 */
export function LanguageGoalSection({ profile, loading, failed }: LanguageGoalSectionProps) {
  const { t } = useTranslation()

  const goalLabel = useMemo(() => {
    if (profile?.goal == null) return null

    return t(`placement.goals.${profile.goal}`)
  }, [profile?.goal, t])

  if (loading) {
    return <Skeleton variant="rectangular" height={72} sx={{ mb: 2 }} />
  }

  if (failed) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('placement.profileLoadError')}
      </Typography>
    )
  }

  const hasIntensity = profile?.days_per_week != null || profile?.minutes_per_day != null
  const hasSlots = profile?.preferred_slots != null && profile.preferred_slots.length > 0

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        {t('placement.goalSectionTitle')}
      </Typography>

      <Stack spacing={1.5}>
        {goalLabel === null && !hasIntensity && !hasSlots && profile?.goal_details == null ? (
          <Typography variant="body2" color="text.secondary">
            {t('placement.goalSectionEmpty')}
          </Typography>
        ) : (
          <>
            {goalLabel !== null && <Field label={t('placement.goal')} value={goalLabel} />}
            {profile?.goal_details != null && profile.goal_details.trim() !== '' && (
              <Field label={t('placement.goalDetails')} value={profile.goal_details} />
            )}
            {profile?.goal_deadline != null && (
              <Field label={t('placement.goalDeadline')} value={formatDateDMY(profile.goal_deadline)} />
            )}
            {hasIntensity && (
              <Field
                label={t('placement.desiredSchedule')}
                value={t('placement.intensityIs', {
                  // Дни и минуты склоняются отдельными ключами: i18next берёт
                  // один `count` на вызов, двумя числами в одной фразе он не
                  // управляет — выходило «3 дней».
                  days:
                    profile?.days_per_week != null
                      ? t('placement.intensityDays', { count: profile.days_per_week })
                      : t('placement.notChosen'),
                  minutes:
                    profile?.minutes_per_day != null
                      ? t('placement.intensityMinutes', { count: profile.minutes_per_day })
                      : t('placement.notChosen'),
                })}
              />
            )}

            {/* Пожелания по времени — то, ради чего учитель обычно и заводит
                разговор на первом уроке. Показываем рядом с интенсивностью:
                «три раза по часу» без времени ещё не расписание */}
            {profile?.preferred_slots != null && profile.preferred_slots.length > 0 && (
              <Field
                label={t('placement.preferredSlots')}
                value={profile.preferred_slots
                  .map((slot) => `${t(`placement.weekdays.${slot.weekday}`)}, ${slot.time}`)
                  .join('; ')}
              />
            )}
          </>
        )}
      </Stack>

      {/* Уровень отделён намеренно: он не правится в анкете — это результат
          теста и правок учителя, а не поле, которое заполняют о себе. */}
      <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
        {t('placement.levelSectionTitle')}
      </Typography>

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        {t('placement.levelSectionHint')}
      </Typography>

      {profile?.has_level ? (
        <Stack spacing={1}>
          <Typography variant="h5" fontWeight={700}>
            {profile.overall_level ?? t('placement.notMeasured')}
          </Typography>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {PROFILE_SKILLS.map((skill) => (
              <Chip
                key={skill}
                size="small"
                variant="outlined"
                label={`${t(`placement.skills.${skill}`)}: ${
                  profile.skill_levels?.[skill]
                  ?? skillMeasurementLabel(profile.skill_measurements?.[skill], t, {
                    isProbe: PROBE_SKILLS.includes(skill),
                  })
                }`}
              />
            ))}
          </Stack>
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {t('placement.notMeasuredYet')}
        </Typography>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1 }}>
        {profile?.self_assessed_level != null && (
          <Typography variant="body2" color="text.secondary">
            {t('placement.selfAssessedIs', { level: profile.self_assessed_level })}
          </Typography>
        )}
        {profile?.assessed_at != null && (
          <Typography variant="body2" color="text.secondary">
            {t('placement.lastTestAt', { date: formatDateDMY(profile.assessed_at) })}
          </Typography>
        )}
      </Stack>
    </Box>
  )
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
        {value}
      </Typography>
    </Box>
  )
}
